import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import { listActiveCompanies } from "../actions/companyActions";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const fmt = (n) =>
  n != null && n !== 0 ? `₹${Number(n).toLocaleString()}` : "-";

const CELL_STYLE = { fontSize: 12, whiteSpace: "nowrap", borderRight: "1px solid #e5e7eb" };

const PayablesMIS = () => {
  const dispatch = useDispatch();
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(currentYear);
  const [company, setCompany] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const companyList = useSelector((state) => state.companyList);
  const { companies = [] } = companyList;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    dispatch(listActiveCompanies());
  }, [dispatch]);

  const handleGenerate = async () => {
    setLoading(true);
    setGenerated(false);
    setRows([]);
    try {
      const params = new URLSearchParams({ month, year });
      if (company) params.set("company", company);

      // Fetch verification report
      const verRes = await fetch(`${API_URL}/price-master/report?${params}`);
      const verData = await verRes.json();

      // Fetch ALL advance records for the month/year (limit=9999 to bypass default pagination of 50)
      const advParams = new URLSearchParams({ month, year, limit: 9999 });
      const advRes = await fetch(`${API_URL}/advance-master?${advParams}`, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
      });
      const advData = await advRes.json();

      if (!verData.success) return;

      const verRecords = verData.data || [];
      const advRecords = advData.records || [];

      // Build lowercase employeeId → state lookup from verification records
      const empStateMap = {};
      verRecords.forEach((r) => {
        if (r.employeeId && r.state)
          empStateMap[r.employeeId.toLowerCase()] = r.state;
      });

      // Aggregate verification data by state (regular + hold separately)
      const stateMap = {};
      verRecords.forEach((r) => {
        const state = r.state;
        if (!state) return;
        if (!stateMap[state]) {
          stateMap[state] = {
            state,
            totalAmount: 0,
            tds: 0,
            amountPayable: 0,
            bobAmt: 0,
            neftAmt: 0,
            totalAdv: 0,
            advAdjusted: 0,
            balAdv: 0,
            holdTotalAmount: 0,
            holdTds: 0,
            holdAmountPayable: 0,
            holdBobAmt: 0,
            holdNeftAmt: 0,
          };
        }

        const isHold = (r.remarksForAccount || "").toLowerCase() === "hold";
        const computedTotal =
          (r.totalAmount ?? 0) +
          (r.totalReimbursement ?? 0) +
          (r.conveyance ?? 0) -
          (r.penalty ?? 0);
        const tdsAmt =
          Math.round(computedTotal * (r.tdsPercent ?? 0) / 100 * 100) / 100;
        const isBoб = (r.bankType || "").toLowerCase() === "bob";

        if (isHold) {
          stateMap[state].holdTotalAmount += computedTotal;
          stateMap[state].holdTds += tdsAmt;
          stateMap[state].holdAmountPayable += r.payableAmount ?? 0;
          if (isBoб) stateMap[state].holdBobAmt += r.payableAmount ?? 0;
          else stateMap[state].holdNeftAmt += r.payableAmount ?? 0;
        } else {
          stateMap[state].totalAmount += computedTotal;
          stateMap[state].tds += tdsAmt;
          stateMap[state].amountPayable += r.payableAmount ?? 0;
          if (isBoб) stateMap[state].bobAmt += r.payableAmount ?? 0;
          else stateMap[state].neftAmt += r.payableAmount ?? 0;
        }
      });

      // Aggregate advance data by state — match employeeCode (case-insensitive) to state via empStateMap
      advRecords.forEach((r) => {
        const code = (r.employeeCode || r.employeeId || "").toLowerCase();
        const state = empStateMap[code];
        if (!state || !stateMap[state]) return;
        stateMap[state].totalAdv += r.advanceAmount ?? 0;
        stateMap[state].advAdjusted += r.deductedAmount ?? 0;
        stateMap[state].balAdv += r.remainingAmount ?? 0;
      });

      setRows(
        Object.values(stateMap).sort((a, b) => a.state.localeCompare(b.state))
      );
      setGenerated(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      totalAmount: acc.totalAmount + r.totalAmount + r.holdTotalAmount,
      tds: acc.tds + r.tds + r.holdTds,
      totalAdv: acc.totalAdv + r.totalAdv,
      advAdjusted: acc.advAdjusted + r.advAdjusted,
      balAdv: acc.balAdv + r.balAdv,
      amountPayable: acc.amountPayable + r.amountPayable + r.holdAmountPayable,
      bobAmt: acc.bobAmt + r.bobAmt + r.holdBobAmt,
      neftAmt: acc.neftAmt + r.neftAmt + r.holdNeftAmt,
    }),
    {
      totalAmount: 0, tds: 0, totalAdv: 0, advAdjusted: 0,
      balAdv: 0, amountPayable: 0, bobAmt: 0, neftAmt: 0,
    }
  );

  const handleExport = () => {
    const exportData = [];
    rows.forEach((r) => {
      exportData.push({
        "TR. PAYABLES MIS": `VERIFICATION CHARGES-${r.state.toUpperCase()}`,
        "Remarks": "",
        "Total Amount": Math.round(r.totalAmount),
        "TDS": Math.round(r.tds),
        "Total Adv": r.totalAdv || 0,
        "Adv Adjusted": r.advAdjusted || 0,
        "Bal Adv": r.balAdv || 0,
        "Amount Payable": Math.round(r.amountPayable),
        "BOB TR. Amt": Math.round(r.bobAmt) || 0,
        "Neft. Amt": Math.round(r.neftAmt),
        "TOTAL": Math.round(r.amountPayable),
      });
      if (r.holdTotalAmount > 0 || r.holdAmountPayable > 0) {
        exportData.push({
          "TR. PAYABLES MIS": `VERIFICATION CHARGES-${r.state.toUpperCase()}`,
          "Remarks": "Hold",
          "Total Amount": Math.round(r.holdTotalAmount),
          "TDS": Math.round(r.holdTds),
          "Total Adv": 0,
          "Adv Adjusted": 0,
          "Bal Adv": 0,
          "Amount Payable": Math.round(r.holdAmountPayable),
          "BOB TR. Amt": Math.round(r.holdBobAmt) || 0,
          "Neft. Amt": Math.round(r.holdNeftAmt),
          "TOTAL": Math.round(r.holdAmountPayable),
        });
      }
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = Object.keys(exportData[0] || {}).map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payables MIS");
    XLSX.writeFile(wb, `payables_mis_${month}_${year}.xlsx`);
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">TR. Payables MIS</h2>
            <p className="text-sm text-gray-500 mt-1">
              State-wise payables summary combining Verification Report &amp; Advance Master
            </p>
          </div>
          {generated && rows.length > 0 && (
            <button
              onClick={handleExport}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2"
            >
              <Download size={18} />
              Export to Excel
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {MONTHS.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Company</InputLabel>
            <Select value={company} label="Company" onChange={(e) => setCompany(e.target.value)}>
              <MenuItem value="">All Companies</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Generating...
              </>
            ) : (
              "Generate MIS"
            )}
          </button>
        </div>

        {/* Empty state */}
        {!generated && !loading && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg">
              Select month, year and company, then click{" "}
              <span className="font-semibold text-green-600">Generate MIS</span>
            </p>
          </div>
        )}

        {/* MIS Table */}
        {generated && (
          <>
            {rows.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg">No data found for selected filters.</p>
              </div>
            ) : (
              <Paper elevation={1}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#fef9c3" }}>
                        {[
                          "TR. PAYABLES MIS",
                          "Remarks",
                          "Total Amount",
                          "TDS",
                          "Total Adv",
                          "Adv Adjusted",
                          "Bal Adv",
                          "Amount Payable",
                          "BOB TR. Amt",
                          "Neft. Amt",
                          "TOTAL",
                        ].map((label) => (
                          <TableCell
                            key={label}
                            style={{
                              fontWeight: "bold",
                              fontSize: 12,
                              whiteSpace: "nowrap",
                              borderRight: "1px solid #e5e7eb",
                              backgroundColor: "#fef9c3",
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <>
                          {/* Regular row */}
                          <TableRow key={r.state} hover>
                            <TableCell style={{ ...CELL_STYLE, fontWeight: 500 }}>
                              VERIFICATION CHARGES-{r.state.toUpperCase()}
                            </TableCell>
                            <TableCell style={CELL_STYLE} />
                            <TableCell style={CELL_STYLE}>{fmt(Math.round(r.totalAmount))}</TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(Math.round(r.tds))}</TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(r.totalAdv)}</TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(r.advAdjusted)}</TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(r.balAdv)}</TableCell>
                            <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#0f766e" }}>
                              {fmt(Math.round(r.amountPayable))}
                            </TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(Math.round(r.bobAmt))}</TableCell>
                            <TableCell style={CELL_STYLE}>{fmt(Math.round(r.neftAmt))}</TableCell>
                            <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#1d4ed8" }}>
                              {fmt(Math.round(r.amountPayable))}
                            </TableCell>
                          </TableRow>

                          {/* Hold row — only shown if this state has hold accounts */}
                          {(r.holdTotalAmount > 0 || r.holdAmountPayable > 0) && (
                            <TableRow key={`${r.state}-hold`} sx={{ backgroundColor: "#fff7ed" }}>
                              <TableCell style={{ ...CELL_STYLE, fontWeight: 500 }}>
                                VERIFICATION CHARGES-{r.state.toUpperCase()}
                              </TableCell>
                              <TableCell style={{ ...CELL_STYLE, color: "#b45309", fontWeight: 600 }}>
                                Hold
                              </TableCell>
                              <TableCell style={CELL_STYLE}>{fmt(Math.round(r.holdTotalAmount))}</TableCell>
                              <TableCell style={CELL_STYLE}>{fmt(Math.round(r.holdTds))}</TableCell>
                              <TableCell style={CELL_STYLE}>-</TableCell>
                              <TableCell style={CELL_STYLE}>-</TableCell>
                              <TableCell style={CELL_STYLE}>-</TableCell>
                              <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#0f766e" }}>
                                {fmt(Math.round(r.holdAmountPayable))}
                              </TableCell>
                              <TableCell style={CELL_STYLE}>{fmt(Math.round(r.holdBobAmt))}</TableCell>
                              <TableCell style={CELL_STYLE}>{fmt(Math.round(r.holdNeftAmt))}</TableCell>
                              <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#1d4ed8" }}>
                                {fmt(Math.round(r.holdAmountPayable))}
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}

                      {/* Totals row */}
                      <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>TOTAL</TableCell>
                        <TableCell style={CELL_STYLE} />
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(Math.round(totals.totalAmount))}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(Math.round(totals.tds))}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(totals.totalAdv)}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(totals.advAdjusted)}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(totals.balAdv)}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#0f766e" }}>{fmt(Math.round(totals.amountPayable))}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(Math.round(totals.bobAmt))}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold" }}>{fmt(Math.round(totals.neftAmt))}</TableCell>
                        <TableCell style={{ ...CELL_STYLE, fontWeight: "bold", color: "#1d4ed8" }}>{fmt(Math.round(totals.amountPayable))}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PayablesMIS;
